import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';

import { getLookupFinanceProvider } from 'PrivateAPI';
import { getEnquiryData } from 'PrivateApis';

import { FinanceSettlementProviderContextType } from '../../DocumentCaptureContexts';

export type UseGetFinanceDataProps = Partial<{
  dpEnquiryId: number;
  onError: () => void;
}>;

type UseGetFinanceData = {
  financeAgreement: FinanceSettlementProviderContextType['financeAgreement'];
  isV5LogBookOwnedBySeller: boolean;
  loading: boolean;
  setFinanceAgreement: Dispatch<SetStateAction<FinanceSettlementProviderContextType['financeAgreement']>>;
}

export const useGetFinanceData = (
  { dpEnquiryId, onError }: UseGetFinanceDataProps,
): UseGetFinanceData => {
  const [financeAgreement, setFinanceAgreement] = useState<FinanceSettlementProviderContextType['financeAgreement']>({});
  const [isV5LogBookOwnedBySeller, setIsV5LogBookOwnedBySeller] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const abortController = useMemo(() => new AbortController(), []);
  const cancelled = useRef<boolean>(false);

  useEffect(() => () => {
    cancelled.current = true;
  }, [abortController]);

  useEffect(() => {
    if (!dpEnquiryId) {
      onError?.();
      return;
    }

    const lookupFinanceData = async () => {
      try {
        const data = await getEnquiryData(dpEnquiryId, abortController);

        const { vehicleDetails: { limitations, v5LogBookOwnedBySeller } } = data;
        const financeAgreements = limitations?.financeAgreements ?? [];

        setIsV5LogBookOwnedBySeller(v5LogBookOwnedBySeller ?? false);

        if (cancelled.current) {
          return;
        }

        if (financeAgreements[0]?.company) {
          await getLookupFinanceProvider(financeAgreements[0]?.company).then((response) => {
            setFinanceAgreement({ automatedFinanceProvider: response.data.provider });
          });
        } else {
          setFinanceAgreement({ optedOut: true });
        }
      } catch {
        if (cancelled.current) {
          return;
        }
        onError?.();
      } finally {
        if (!cancelled.current) {
          setLoading(false);
        }
      }
    };

    if (!financeAgreement.automatedFinanceProvider && !financeAgreement.optedOut) {
      lookupFinanceData();
    }
  }, [abortController, dpEnquiryId, financeAgreement, onError]);

  return {
    financeAgreement,
    isV5LogBookOwnedBySeller,
    loading,
    setFinanceAgreement,
  };
};
